import { Avatar } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';

import * as ItemUtils from '../common/ItemTools';
import SelectionOverlay from './SelectionOverlay';
import VerdictBar, { useVerdictTransition } from './common/VerdictBar';
import {
  selectSelectionMode, selectSelectedIds, toggleSelection,
} from '../redux/features/context/contextSlice';
import { SERIF_STACK } from './common/tokens';
import HeatBuzzBadge from './common/HeatBuzzBadge';
import CardIdentity from './common/card/CardIdentity';
import useCardMenu from './common/card/useCardMenu';
import useCardDensity, { CardDensityProvider } from './common/card/useCardDensity';
import buildEntityMenuOptions from './common/card/entityMenu';
import { entityFacts, timeFacts, typeLabel } from './common/card/facts';
import CardFooter from './common/card/CardFooter';
import CardFavoriteAction from './common/card/CardFavoriteAction';
import CardShareAction from './common/card/CardShareAction';
import {
  ActionsBox, CompactActionButton, EntityCard, VerdictRow,
} from './common/card/styles';

const Portrait = styled(Avatar)(({ theme }) => ({
  width: 'auto',
  height: '100%',
  aspectRatio: '1 / 1',
  maxWidth: '100%',
  fontFamily: SERIF_STACK,
  fontWeight: 'bold',
  fontSize: 'clamp(2rem, 8vw, 6rem)',
  cursor: 'pointer',
  border: `3px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[2],
}));

export default function AuthorCard(props) {
  const {
    author,
    onAuthorVerdict,
    onAuthorEdit,
    onAuthorDelete,
    onAuthorUnshare,
    onAuthorShare,
    onAuthorFavorite,
    onAuthorRead,
    onAuthorMerge,
  } = props;

  const dispatch = useDispatch();
  const { cardRef, shows } = useCardDensity();
  const selectionMode = useSelector(selectSelectionMode);
  const selectedIds = useSelector(selectSelectedIds);
  const isSelected = selectedIds.includes(author.id);
  const onToggleSelect = () => dispatch(toggleSelection(author.id));

  const moderating = Boolean(onAuthorVerdict);
  const showVerdictBar = moderating && !selectionMode;
  const handleDecide = React.useCallback(
    (kind, reason) => onAuthorVerdict && onAuthorVerdict(author, kind, reason),
    [onAuthorVerdict, author],
  );
  const { decide, sx: verdictSx } = useVerdictTransition(handleDecide);

  const [, drag] = useDrag(() => ({
    type: 'author',
    item: { name: author.id },
    end: (dragItem, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dragItem && dropResult && onAuthorMerge) {
        onAuthorMerge(dropResult.name, [dragItem.name]);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }), [author.id, onAuthorMerge]);

  const [, drop] = useDrop(() => ({
    accept: 'author',
    drop: () => ({ name: author.id }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [author.id]);

  // The root is both the drag source and what the ladder measures.
  const setCardNode = React.useCallback((node) => {
    cardRef.current = node;
    drag(node);
  }, [cardRef, drag]);

  const menuOptions = buildEntityMenuOptions({
    target: author,
    hasPrivateData: Boolean(author.hasPrivateData),
    hasPublicData: Boolean(author.hasPublicData),
    isFavorite: Boolean(author.isFavorite),
    shareDiff: author.shareDiff,
    moderating,
    collapsed: !shows('actions'),
    onEdit: onAuthorEdit,
    onDelete: onAuthorDelete,
    onUnshare: onAuthorUnshare,
    onShare: onAuthorShare,
    onFavorite: onAuthorFavorite,
  });
  const { open: openMenu, menu } = useCardMenu(menuOptions);

  const stats = author.stats || {};
  const times = timeFacts(author);
  const facts = entityFacts({
    lead: typeLabel(author),
    age: times.published || times.created,
    stats,
    details: shows('factDetails'),
  });
  const { heatScore, buzzScore } = ItemUtils.getEntityHeatBuzz(author);

  const canShare = onAuthorShare && !moderating && author.hasPrivateData;
  const shareIsMarker = Boolean(author.hasPublicData)
    && ItemUtils.shareChangesState(author.shareDiff) === 'none';

  const imageUrl = ItemUtils.getAuthorImageUrl(author, author.authorImageScope);
  const sourceLetter = ItemUtils.getSourceLetter(author);
  const authorName = ItemUtils.getAuthorInfoName(author);
  const avatarName = authorName || sourceLetter || '?';

  return (
    <CardDensityProvider shows={shows}>
      <EntityCard raised ref={setCardNode} sx={moderating ? verdictSx : undefined}>
        <SelectionOverlay
          selectionMode={selectionMode}
          selected={isSelected}
          onToggle={onToggleSelect}
        />
        <CardIdentity
          onOpen={() => onAuthorRead(author)}
          title={authorName}
          facts={facts}
          excerpt={shows('excerpt') ? author.excerpt : null}
          media={(
            <Portrait
              src={imageUrl}
              ref={drop}
              sx={imageUrl ? undefined : { bgcolor: ItemUtils.authorColor(avatarName) }}
            >
              {ItemUtils.authorInitials(avatarName)}
            </Portrait>
          )}
          overlay={shows('heatBadge') ? <HeatBuzzBadge heat={heatScore} buzz={buzzScore} /> : null}
        />
        <CardFooter>
          <ActionsBox>
            {canShare && (shareIsMarker ? shows('markers') : shows('actions')) && (
              <CardShareAction
                hasPublicData={Boolean(author.hasPublicData)}
                changes={ItemUtils.shareChangesState(author.shareDiff)}
                title={ItemUtils.shareActionTitle(Boolean(author.hasPublicData), author.shareDiff)}
                onShare={() => onAuthorShare(author)}
              />
            )}
            {(onAuthorFavorite ? shows('actions') : shows('markers')) && (
              <CardFavoriteAction
                isFavorite={Boolean(author.isFavorite)}
                onToggle={onAuthorFavorite && (() => onAuthorFavorite(author))}
              />
            )}
            {menuOptions.length > 0 && !showVerdictBar && shows('menu') && (
              <CompactActionButton onClick={openMenu}>
                <MoreVertIcon />
              </CompactActionButton>
            )}
          </ActionsBox>
        </CardFooter>
        {showVerdictBar && (
          <VerdictRow>
            <VerdictBar onDecide={decide} menuOptions={menuOptions} />
          </VerdictRow>
        )}
        {menu}
      </EntityCard>
    </CardDensityProvider>
  );
}

AuthorCard.propTypes = {
  onAuthorVerdict: PropTypes.func,
  onAuthorEdit: PropTypes.func,
  onAuthorDelete: PropTypes.func,
  onAuthorUnshare: PropTypes.func,
  onAuthorShare: PropTypes.func,
  onAuthorFavorite: PropTypes.func,
  onAuthorRead: PropTypes.func,
  onAuthorMerge: PropTypes.func,
};

AuthorCard.defaultProps = {
  onAuthorVerdict: null,
  onAuthorEdit: null,
  onAuthorDelete: null,
  onAuthorUnshare: null,
  onAuthorShare: null,
  onAuthorFavorite: null,
  onAuthorRead: null,
  onAuthorMerge: null,
};
